import { i18n } from '~/utils';
import type { Locale } from '~/utils/i18n';

/**
 * Formats a date range, omitting redundant year/month from the start date
 * when they match the end date.
 * - Same year: "02.02. – 30.06.2026"
 * - Same year & month: "02. – 30.06.2026"
 * - From only: "Ab 01.01.2026"
 */
export function formatDateRange(
  from: string | null | undefined,
  until: string | null | undefined,
  locale: Locale = 'de',
) {
  const fromDate = from ? new Date(from) : null;
  const untilDate = until ? new Date(until) : null;

  // Guard against invalid date strings (e.g. "not-a-date" → Invalid Date)
  if (fromDate && isNaN(fromDate.getTime())) return '⸺';
  if (untilDate && isNaN(untilDate.getTime())) return '⸺';

  if (!fromDate && !untilDate) {
    return '—';
  }

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });

  const fromDateFormatted = fromDate ? dateFormatter.format(fromDate) : null;
  const untilDateFormatted = untilDate ? dateFormatter.format(untilDate) : null;

  if (fromDateFormatted === untilDateFormatted) {
    return fromDateFormatted;
  }

  if (fromDate && !untilDate) {
    return i18n.t(($) => $.validityPeriodFrom, {
      date: dateFormatter.format(fromDate),
      ns: 'contract',
    });
  }

  if (!fromDate && untilDate) {
    return i18n.t(($) => $.validityPeriodUntil, {
      date: dateFormatter.format(untilDate),
      ns: 'contract',
    });
  }

  if (fromDate && untilDate) {
    const sameYear = fromDate.getFullYear() === untilDate.getFullYear();
    const sameMonth = sameYear && fromDate.getMonth() === untilDate.getMonth();

    const fromFormatter = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: sameMonth ? undefined : 'numeric',
      year: sameYear ? undefined : 'numeric',
    });
    const fromFormatted = fromFormatter.format(fromDate);
    const fromDateAbbreviatedFormatted = sameYear
      ? `${fromFormatted}.`.replace(/\.{2,}/g, '.')
      : fromFormatted;

    return [fromDateAbbreviatedFormatted, untilDateFormatted]
      .filter(Boolean)
      .join(' – ');
  }

  return '—';
}
